import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import type { NodeContent, LearningNode } from '@shared/types/learning-path';
import { Checkpoint } from './Checkpoint';
import { VideoEmbed } from './VideoEmbed';
import { DiagramRenderer } from './DiagramRenderer';

// Loose type for what DiagramRenderer expects — matches the visual_spec
// shape produced by LA-002 plus the optional Haiku-validation hints.
type VisualSpecForRenderer = { type: string; data?: Record<string, unknown>; caption?: string };

interface NodeContentRendererProps {
  nodeContent: NodeContent;
  node: LearningNode;
  pathId: string;
}

// LA-002 section type (from rendered_content.sections)
// LA-002 can return sections in different formats depending on how Claude structures them
interface ContentSection {
  // Standard format
  type?: string;
  content?: string;
  // Alternative format (actual LA-002 output)
  section_type?: string;
  section_id?: string;
  heading?: string;
  body?: string;
  // Visual/exercise/checkpoint
  visual_spec?: { type: string; data: Record<string, unknown>; caption?: string; imageUrl?: string; imageStatus?: string };
  exercise_spec?: { problem_statement: string; steps: string[]; practice_variant: string; expected_answer: string };
  checkpoint_spec?: Record<string, unknown>;
  viewpoint_toggle?: { label: string; content: string };
}

function getSections(content: NodeContent['content']): ContentSection[] {
  const raw = content as Record<string, unknown>;
  if (Array.isArray(raw.sections)) return raw.sections as ContentSection[];
  return [];
}

function normaliseForCompare(text: string): string {
  return text.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/**
 * On a checkpoint node, LA-002 emits the prompt twice — once as an `exercise`
 * section and again as `checkpointData`. Rendering both showed the learner the
 * identical question in two stacked boxes, the second one answerable and the
 * first one dead. Drop the exercise section when it restates the checkpoint.
 *
 * Deliberately conservative: only an exercise whose problem statement actually
 * matches the checkpoint question is hidden, so a node carrying a genuinely
 * different practice exercise alongside its checkpoint still renders both.
 */
function duplicatesCheckpoint(section: ContentSection, question: string | undefined): boolean {
  const problem = section.exercise_spec?.problem_statement;
  if (!problem || !question) return false;
  const a = normaliseForCompare(problem);
  const b = normaliseForCompare(question);
  if (a.length < 40 || b.length < 40) return false; // too short to judge safely
  return a === b || a.includes(b) || b.includes(a);
}

export function NodeContentRenderer({ nodeContent, node, pathId }: NodeContentRendererProps) {
  const { content } = nodeContent;
  const sections = getSections(content).filter(
    (s) => !duplicatesCheckpoint(s, content.checkpointData?.question),
  );

  return (
    <div className="space-y-6">
      {/* Title */}
      {content.title && (
        <h2 className="font-heading font-bold text-xl sm:text-2xl">
          {content.title}
        </h2>
      )}

      {/* Key terms */}
      {(() => {
        const raw = content as Record<string, unknown>;
        const terms = (content.keyTermsDefined ?? raw.key_terms_defined) as Array<{ term: string; definition: string }> | null;
        if (!terms || terms.length === 0) return null;
        return (
        <div className="flex flex-wrap gap-2">
          {terms.map((kt) => (
            <span
              key={kt.term}
              className="px-2 py-1 rounded-sm bg-indigo-500/10 text-indigo-300 text-xs font-body"
              title={kt.definition}
            >
              {kt.term}
            </span>
          ))}
        </div>
        );
      })()}

      {/* Markdown content */}
      {content.markdown && typeof content.markdown === 'string' && (
        <div className="prose-cogniprint">
          <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
            {content.markdown}
          </ReactMarkdown>
        </div>
      )}

      {/* Sections (from LA-002 — handles both standard and heading+body formats) */}
      {sections.length > 0 && (
        <div className="space-y-6">
          {sections.map((section, i) => {
            const sectionType = section.type ?? section.section_type ?? 'prose';
            const rawText = section.content ?? section.body ?? '';
            // Ensure sectionText is always a string — LA-002 sometimes returns objects
            const sectionText = typeof rawText === 'string' ? rawText : JSON.stringify(rawText, null, 2);
            const sectionHeading = typeof section.heading === 'string' ? section.heading : undefined;

            return (
            <div key={i}>
              {/* Heading + Body format (actual LA-002 output) */}
              {sectionText && !section.visual_spec && !section.exercise_spec && sectionType !== 'reflection' && (
                <div>
                  {sectionHeading && (
                    <h3 className="font-heading font-semibold text-lg mb-3">{sectionHeading}</h3>
                  )}
                  <div className="prose-cogniprint">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                      {sectionText}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {sectionType === 'visual' && section.visual_spec && (
                <div className="p-4 rounded-lg bg-dark-subtle border border-dark-border">
                  <p className="text-xs text-white/60 font-body uppercase tracking-wider mb-3">
                    {section.visual_spec.type}
                  </p>
                  <DiagramRenderer visualSpec={section.visual_spec as VisualSpecForRenderer} />
                  {sectionText && (
                    <div className="prose-cogniprint prose-on-dark mt-3">
                      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                        {sectionText}
                      </ReactMarkdown>
                    </div>
                  )}
                  {section.visual_spec.caption && (
                    <p className="text-xs text-white/70 font-body mt-3 text-center italic">
                      {section.visual_spec.caption}
                    </p>
                  )}
                </div>
              )}

              {sectionType === 'exercise' && section.exercise_spec && (
                <div className="p-5 rounded-lg bg-dark-surface border border-indigo-500/30">
                  <p className="text-xs text-indigo-400 font-body uppercase tracking-wider mb-3 font-semibold">Exercise</p>
                  <div className="prose-cogniprint">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                      {section.exercise_spec.problem_statement}
                    </ReactMarkdown>
                  </div>
                </div>
              )}

              {sectionType === 'reflection' && sectionText && (
                <div className="p-5 rounded-lg bg-dark-surface border-l-[3px] border-l-gold-500">
                  <p className="text-xs text-gold-500 font-body uppercase tracking-wider mb-2 font-semibold">Reflection</p>
                  <p className="text-content-secondary font-body italic">{sectionText}</p>
                </div>
              )}

              {section.viewpoint_toggle && (
                <details className="mt-3 rounded-lg bg-dark-subtle border border-dark-border">
                  <summary className="px-4 py-3 text-sm text-indigo-400 font-body cursor-pointer hover:text-indigo-300 transition-colors">
                    {section.viewpoint_toggle.label}
                  </summary>
                  <div className="px-4 pb-4 prose-cogniprint prose-on-dark">
                    <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeHighlight]}>
                      {section.viewpoint_toggle.content}
                    </ReactMarkdown>
                  </div>
                </details>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Checkpoint */}
      {content.checkpointData && (
        <Checkpoint
          checkpointData={content.checkpointData}
          nodeId={node.nodeId}
          pathId={pathId}
        />
      )}

      {/* Videos */}
      {content.videoResults && content.videoResults.length > 0 && (
        <div className="space-y-4">
          {content.videoResults.map((video) => (
            <VideoEmbed
              key={video.videoId}
              video={video}
              nodeId={node.nodeId}
              pathId={pathId}
            />
          ))}
        </div>
      )}

      {/* Reading time */}
      {content.estimatedReadTimeMinutes > 0 && (
        <p className="text-xs text-content-dim font-body pt-4 border-t border-dark-border">
          ~{content.estimatedReadTimeMinutes} min read · {content.wordCount} words
        </p>
      )}
    </div>
  );
}
